import React from 'react';
import { Zap, Trophy, Calendar, Target } from 'lucide-react';

const RulesPage = ({ onPlayClick }) => {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Clean Purple Gradient Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          background: 'linear-gradient(180deg, #0A0512 0%, #1a0b2e 50%, #2d1554 100%)'
        }}
      >
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-12 max-w-6xl">
        {/* Header - Top Left Aligned */}
        <div className="mb-16 fade-in">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
            Fireworks Community
          </h1>
          <h2 className="text-sm sm:text-base text-[#A19EB5] max-w-4xl leading-relaxed font-normal">
            The friendly competition has concluded. DeepSeek V4 was released April 24th and went live on Fireworks on April 27th. Winners have received credits in their accounts. If any questions, please contact us at <a href="mailto:marketing@fireworks.ai" className="text-[#8E24F5] hover:text-[#A64DF7] underline transition-colors">marketing@fireworks.ai</a>.
          </h2>
        </div>

        {/* Game Title */}
        <div className="text-center mb-16 fade-in" style={{ animationDelay: '0.1s' }}>
          <h3 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4">
            DeepSeek 4<br />
            <span className="text-gradient">Prediction Pool</span>
          </h3>
          <p className="text-lg text-[#A19EB5] max-w-2xl mx-auto leading-relaxed">
            Predict the future of AI. Win Fireworks credits. 100% free to play.
          </p>
        </div>

        {/* Rules Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12 fade-in" style={{ animationDelay: '0.2s' }}>
          {/* How It Works */}
          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8E24F5] to-[#A64DF7] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">How It Works</h3>
            </div>
            <ul className="space-y-3 text-[#A19EB5] leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Free to play - no purchase necessary</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>You have <strong className="text-white">$100</strong> in virtual chips to wager</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Distribute across <strong className="text-white">3 prediction bets</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Winners share the prize pool proportionally</span>
              </li>
            </ul>
          </div>

          {/* The Bets */}
          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8E24F5] to-[#A64DF7] flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">The Bets</h3>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-1 font-semibold">Bet #1</div>
                <p className="text-[#A19EB5]">Will DeepSeek 4 launch in <strong className="text-white">April 2026</strong>?</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-1 font-semibold">Bet #2</div>
                <p className="text-[#A19EB5]">Will it exceed <strong className="text-white">90%</strong> on SWE Bench Verified?</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-1 font-semibold">Bet #3</div>
                <p className="text-[#A19EB5]">Pick the exact <strong className="text-white">release date</strong></p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8E24F5] to-[#A64DF7] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">Timeline</h3>
            </div>
            <ul className="space-y-3 text-[#A19EB5] leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Bets lock <strong className="text-white">Monday morning</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Results announced after DeepSeek 4 release</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>If no release by <strong className="text-white">May 10th</strong>, "No" bets win</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>You can edit your bets anytime before lock</span>
              </li>
            </ul>
          </div>

          {/* Prizes */}
          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8E24F5] to-[#A64DF7] flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">Prizes</h3>
            </div>
            <ul className="space-y-3 text-[#A19EB5] leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>All participants get <strong className="text-white">$4 Fireworks credits</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Winners earn additional credits</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Prize split proportional to wagers (Jeopardy-style)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#A64DF7] mt-1">•</span>
                <span>Credits distributed manually after results</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center fade-in" style={{ animationDelay: '0.4s' }}>
          <button
            data-testid="play-button"
            onClick={onPlayClick}
            className="purple-btn text-lg px-12 py-4 glow"
          >
            Place Your Bets
          </button>
          <p className="mt-6 text-xs text-[#6E6885] leading-relaxed max-w-2xl mx-auto">
            This page bypassed code-review. If any questions, please email <a href="mailto:marketing@fireworks.ai" className="text-[#8E24F5] hover:text-[#A64DF7] underline transition-colors">marketing@fireworks.ai</a> and know that our serverless inference endpoints are much more reliable.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RulesPage;
